import { UpdatePipeline, type PipelinePhase } from "../office/UpdatePipeline";
export type { PipelinePhase } from "../office/UpdatePipeline";
import { OfficeDownload } from "../office/OfficeDownload";
import { dispatch } from "../utils/events";
import { onOfficeReady } from "../office/state";
import Alpine from "alpinejs";
import { DEBOUNCE_UPDATE_PIPELINE_MS, DEBOUNCE_UPDATE_PIPELINE_MULTIPLIER } from "../timing";

import { modificationHandler } from "./modifications";

export const updateState = Alpine.reactive({ phase: "unregistered" as PipelinePhase });

export const pipeline = new UpdatePipeline();
pipeline.onChange((phase) => {
  updateState.phase = phase;
});
modificationHandler.onModification(() => {
  pipeline.onModified()
})

export function bindHtmxUpdate(el: Node) {
  const cleanup = new AbortController();

  el.addEventListener('htmx:beforeSend', (event: any) => {
    const signal = pipeline.signal ?? null
    if (signal === null) return

    const xhr = event.detail.xhr as XMLHttpRequest
    el.addEventListener('htmx:afterRequest', (event: any) => {
      if (xhr !== event.detail.xhr) return

      if (event.detail.successful) {
        pipeline.onUploadComplete();
        return
      }

      pipeline.onUploadError(createUploadError(xhr))
    }, { once: true, signal: cleanup.signal })
  }, { signal: cleanup.signal })

  el.addEventListener('htmx:afterCleanupElement', () => {
    cleanup.abort()
  }, { once: true })
}

export function bindFormUpdate(form: HTMLFormElement) {
  form.addEventListener('submit', async (event) => {
    event.preventDefault()
    const signal = pipeline.signal ?? null
    if (signal === null) return

    const formData = new FormData(form)

    try {
      const response = await fetch(form.action, {
        method: form.method || 'POST',
        body: formData,
      })

      if (response.ok) {
        pipeline.onUploadComplete()
        return
      }

      pipeline.onUploadError(createUploadError(response))
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }

      pipeline.onUploadError(error instanceof Error ? error : new Error(String(error)))
      throw error
    }
  })
}

function createUploadError(response: Pick<Response | XMLHttpRequest, 'status' | 'statusText'>): Error {
  const statusText = response.statusText ? ` ${response.statusText}` : ''
  return new Error(`Upload failed with status ${response.status}${statusText}`)
}

function registerPipeline(): void {
  const download = new OfficeDownload(Office.context);

  pipeline.register({
    debounceMs: DEBOUNCE_UPDATE_PIPELINE_MS,
    debounceMultiplier: DEBOUNCE_UPDATE_PIPELINE_MULTIPLIER,
    download,
    onReady(file, signal) {
      const dt = new DataTransfer();
      dt.items.add(file);
      dispatch("document-download-completed", { files: dt.files, signal });
    },
  });

}

// Initialize pipeline when Office is ready
onOfficeReady((enabled) => {
  if (enabled) {
    registerPipeline();
  }
});
